/**
 * Notification template list state machine - replaces useNotificationTemplateStore (Zustand)
 */
import { setup, assign } from 'xstate'
import type {
  NotificationTemplate,
  NotificationTrigger,
  NotificationType,
  SentNotification,
} from '../types'

export interface NotificationTemplateListContext {
  templates: NotificationTemplate[]
  sentNotifications: SentNotification[]
  selectedTemplateId: string | null
}

export type NotificationTemplateListEvent =
  | { type: 'SET_TEMPLATES'; templates: NotificationTemplate[] }
  | { type: 'ADD_TEMPLATE'; template: NotificationTemplate }
  | { type: 'UPDATE_TEMPLATE'; id: string; updates: Partial<NotificationTemplate> }
  | { type: 'DELETE_TEMPLATE'; id: string }
  | { type: 'SET_SELECTED_TEMPLATE_ID'; id: string | null }
  | { type: 'ADD_SENT_NOTIFICATION'; notification: SentNotification }
  | {
      type: 'UPDATE_SENT_NOTIFICATION'
      id: string
      updates: Partial<SentNotification>
    }
  | { type: 'CLEAR' }

const machineSetup = setup({
  types: {
    context: {} as NotificationTemplateListContext,
    events: {} as NotificationTemplateListEvent,
  },
})

export const notificationTemplateListMachine = machineSetup.createMachine({
  id: 'notificationTemplateList',
  context: {
    templates: [],
    sentNotifications: [],
    selectedTemplateId: null,
  },
  on: {
    SET_TEMPLATES: {
      actions: assign({
        templates: ({ event }) => (event.type === 'SET_TEMPLATES' ? event.templates : []),
      }),
    },
    ADD_TEMPLATE: {
      actions: assign({
        templates: ({ context, event }) =>
          event.type === 'ADD_TEMPLATE'
            ? [...context.templates, event.template]
            : context.templates,
      }),
    },
    UPDATE_TEMPLATE: {
      actions: assign({
        templates: ({ context, event }) => {
          if (event.type !== 'UPDATE_TEMPLATE') return context.templates
          return context.templates.map((t) =>
            t.id === event.id ? { ...t, ...event.updates, updatedAt: Date.now() } : t
          )
        },
      }),
    },
    DELETE_TEMPLATE: {
      actions: assign({
        templates: ({ context, event }) => {
          if (event.type !== 'DELETE_TEMPLATE') return context.templates
          return context.templates.filter((t) => t.id !== event.id)
        },
      }),
    },
    SET_SELECTED_TEMPLATE_ID: {
      actions: assign({
        selectedTemplateId: ({ event }) =>
          event.type === 'SET_SELECTED_TEMPLATE_ID' ? event.id : null,
      }),
    },
    ADD_SENT_NOTIFICATION: {
      actions: assign({
        sentNotifications: ({ context, event }) =>
          event.type === 'ADD_SENT_NOTIFICATION'
            ? [...context.sentNotifications, event.notification]
            : context.sentNotifications,
      }),
    },
    UPDATE_SENT_NOTIFICATION: {
      actions: assign({
        sentNotifications: ({ context, event }) => {
          if (event.type !== 'UPDATE_SENT_NOTIFICATION') return context.sentNotifications
          return context.sentNotifications.map((n) =>
            n.id === event.id ? { ...n, ...event.updates } : n
          )
        },
      }),
    },
    CLEAR: {
      actions: assign({
        templates: [],
        sentNotifications: [],
        selectedTemplateId: null,
      }),
    },
  },
})
